/**
 * Permissive UK phone capture for sales-assisted lead forms.
 *
 * The design rule is asymmetric on purpose: an optional phone field on a paid
 * landing page must never be the reason a real enquiry is lost, so this accepts
 * every plausible UK number as typed — 07 mobiles, 01/02/03 landlines, +44 and
 * 0044 forms, and any mix of spaces, brackets, hyphens and dots — and rejects
 * only input that cannot be a phone number at all (letters, no plausible digit
 * count). Formatting is never grounds for rejection.
 *
 * Two values come back because they answer different questions. `raw` is what
 * the enquirer typed and is what a human should see; `e164` is the unambiguous
 * dialling form, and is deliberately nullable — an accepted number we cannot
 * confidently normalise (a foreign number, an unassigned UK range) keeps its
 * raw value rather than being thrown away.
 *
 * Related: `isPlausiblePhone` in lib/format.ts is the NON-blocking warning
 * check for project contact fields. It shares this permissive character set so
 * a "warn" there never contradicts an "accept" here; this module adds the E.164
 * normalisation that check has no need for.
 */

/**
 * Superset of lib/format.ts `PHONE_CHARS_RE` — the dot is allowed here because
 * `07700.900123` is a plausible number and formatting must not lose a lead.
 */
const ALLOWED_CHARACTERS = /^[\d\s+().-]+$/;

/** E.164 allows at most 15 digits; below 9 no UK number is possible. */
const MIN_DIGITS = 9;
const MAX_DIGITS = 15;

/** Bound the stored value so no field is unbounded. Matches the setup-service lead. */
export const MAX_PHONE_INPUT_LENGTH = 50;

/**
 * A UK national significant number: 9 or 10 digits with the trunk `0` removed.
 * Leading 4 and 6 are unassigned by Ofcom, so their absence here is what lets
 * a bare `44…` be read as a country code rather than an area code.
 */
const UK_NATIONAL_SIGNIFICANT_NUMBER = /^[1235789]\d{8,9}$/;

export interface NormalisedPhone {
  /** Exactly what the enquirer typed, trimmed with whitespace runs collapsed. */
  raw: string;
  /** Unambiguous dialling form, or null when it cannot be derived confidently. */
  e164: string | null;
}

/**
 * Drop the trunk `0` that UK convention keeps inside international forms —
 * `+44 (0)7700 900123` is the single commonest way a UK number is written down.
 */
function ukNationalSignificantNumber(digits: string): string | null {
  const nsn = digits.startsWith("0") ? digits.slice(1) : digits;
  return UK_NATIONAL_SIGNIFICANT_NUMBER.test(nsn) ? nsn : null;
}

function toE164(raw: string, digits: string): string | null {
  if (raw.startsWith("+")) {
    if (!digits.startsWith("44")) {
      // Another country's number. A bracketed "(0)" is the same
      // drop-this-for-international-dialling trunk-prefix convention already
      // handled for the UK above — honour it generically here too, or a
      // number written the same way (`+353 (0)87 123 4567`) gains a spurious
      // extra digit instead of the dialable `+353871234567`.
      const internationalDigits = raw
        .replace(/\(0\)/g, "")
        .replace(/\D/g, "");
      // A country code can never start with 0 — an ITU-T structural rule,
      // true for every country, not a case-by-case exception. Free to check
      // without a country-code table, and worth checking: emitting a
      // malformed value here would displace the working raw fallback in the
      // Slack alert, which prefers phoneE164 whenever it is set.
      return /^[1-9]/.test(internationalDigits)
        ? `+${internationalDigits}`
        : null;
    }
    const nsn = ukNationalSignificantNumber(digits.slice(2));
    return nsn ? `+44${nsn}` : null;
  }

  if (digits.startsWith("0044")) {
    const nsn = ukNationalSignificantNumber(digits.slice(4));
    return nsn ? `+44${nsn}` : null;
  }

  // A bare `44…` long enough to carry a full national number is a country code
  // typed without the plus; at 10 digits or fewer it is read as a UK number,
  // which the unassigned-leading-digit rule then rejects for normalisation.
  if (digits.startsWith("44") && digits.length > 10) {
    const nsn = ukNationalSignificantNumber(digits.slice(2));
    return nsn ? `+44${nsn}` : null;
  }

  const nsn = ukNationalSignificantNumber(digits);
  return nsn ? `+44${nsn}` : null;
}

/**
 * Normalise a typed phone number, or return null when the input is not a phone
 * number at all. An empty or whitespace-only value is "not provided", not junk —
 * callers treat null the same way for both and must not surface an error for a
 * field the enquirer simply left blank.
 */
export function normaliseUkPhone(
  input: string | null | undefined,
): NormalisedPhone | null {
  if (typeof input !== "string") return null;

  const raw = input.trim().replace(/\s+/g, " ");
  if (!raw || raw.length > MAX_PHONE_INPUT_LENGTH) return null;
  if (!ALLOWED_CHARACTERS.test(raw)) return null;

  // A plus is only meaningful as the international prefix.
  const plusCount = (raw.match(/\+/g) ?? []).length;
  if (plusCount > 1 || (plusCount === 1 && !raw.startsWith("+"))) return null;

  const digits = raw.replace(/\D/g, "");
  if (digits.length < MIN_DIGITS || digits.length > MAX_DIGITS) return null;

  return { raw, e164: toE164(raw, digits) };
}

/**
 * True when a value can be accepted for storage. Blank passes — the field is
 * optional, and a blank optional field is never an error.
 */
export function isAcceptablePhoneInput(
  input: string | null | undefined,
): boolean {
  if (input == null || input.trim() === "") return true;
  return normaliseUkPhone(input) !== null;
}
